import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { isRetryableRemoteError, MFE_ERROR_NAMES, RemoteFallbackProps } from 'mfe/runtime';

type InventoryRemoteErrorProps = RemoteFallbackProps & {
  className?: string;
};

/**
 * Recoverable error view for the inventory microfrontend, shown when it fails to load or crashes
 * while rendering. A timeout gets its own copy, and a remote that cannot be fixed by retrying only
 * offers the way home.
 * @param {InventoryRemoteErrorProps} props - Component properties
 */
export const InventoryRemoteError = ({ className, error, onRetry }: InventoryRemoteErrorProps) => {
  const { t } = useTranslation('mfe');
  const history = useHistory();
  const moduleName = t('mfe:modules.inventory');
  const isTimeout = error.name === MFE_ERROR_NAMES.TIMEOUT;

  const description = () => {
    if (isTimeout) return t('mfe:remoteError.timeoutDescription');
    if (!isRetryableRemoteError(error)) return t('mfe:remoteError.unavailableDescription');
    return t('mfe:remoteError.description');
  };

  return (
    <Empty className={cn('h-full font-sans', className)} data-testid="inventory-remote-error">
      <EmptyHeader>
        <EmptyTitle>
          {t(isTimeout ? 'mfe:remoteError.timeoutTitle' : 'mfe:remoteError.title', { module: moduleName })}
        </EmptyTitle>
        <EmptyDescription>{description()}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="flex-row justify-center gap-2">
        {onRetry && <Button onClick={onRetry}>{t('mfe:remoteError.retry')}</Button>}
        <Button onClick={() => history.push('/')} variant={onRetry ? 'outline' : 'default'}>
          {t('mfe:remoteError.goHome')}
        </Button>
      </EmptyContent>
    </Empty>
  );
};
